/**
 * Stable identifiers shared by every package. Adding a role = add its id here, a RoleDef in roles.ts,
 * strings in strings/en.json, art in the asset manifest and a night program in @onuw/engine.
 */
export const ROLE_IDS = [
  // wake early
  'oracle',
  'copycat',
  'doppelganger',
  // dusk: hand out marks before the night
  'vampire',
  'count',
  'master',
  'renfield',
  'diseased',
  'cupid',
  'instigator',
  'priest',
  'assassin',
  'appAssassin',
  'sentinel',
  'alien',
  'syntheticAlien',
  'groob',
  'zerb',
  'bodySnatcher',
  // wolves
  'werewolf',
  'giantWolf',
  'alphaWolf',
  'mysticWolf',
  // indicators
  'minion',
  'appTanner',
  'leader',
  'cow',
  'mason',
  'thing',
  // viewers
  'seer',
  'appSeer',
  'paranormalInvestigator',
  'marksman',
  'nostradamus',
  'psychic',
  // changers
  'robber',
  'witch',
  'pickpocket',
  'troublemaker',
  'villageIdiot',
  'auraSeer',
  'gremlin',
  'rascal',
  'drunk',
  // final viewers / revealers
  'insomniac',
  'beholder',
  'squire',
  'revealer',
  'exposer',
  'empath',
  'blob',
  'mortician',
  'curator',
  // no night action
  'villager',
  'dreamWolf',
  'hunter',
  'bodyguard',
  'prince',
  'tanner',
  'cursed',
] as const
export type RoleId = (typeof ROLE_IDS)[number]
export const isRoleId = (x: unknown): x is RoleId =>
  typeof x === 'string' && (ROLE_IDS as readonly string[]).includes(x)

export const TEAM_IDS = [
  'village',
  'werewolves',
  'tanner',
  'alien',
  'mortician',
  'blob',
  'vampire',
  'assassin',
  'synthetic',
  'variable',
] as const
export type TeamId = (typeof TEAM_IDS)[number]

export const EXPANSION_IDS = ['base', 'daybreak', 'bonus', 'vampire', 'alien', 'custom'] as const
export type ExpansionId = (typeof EXPANSION_IDS)[number]

export const ARTIFACT_IDS = [
  'clawOfTheWerewolf',
  'brandOfTheVillager',
  'cudgelOfTheTanner',
  'bowOfTheHunter',
  'swordOfTheBodyguard',
  'cloakOfThePrince',
  'voidOfNothingness',
  'maskOfMuting',
  'shroudOfShame',
] as const
export type ArtifactId = (typeof ARTIFACT_IDS)[number]

export const MARK_IDS = [
  'markOfLove',
  'markOfFear',
  'markOfVampire',
  'markOfBat',
  'markOfDisease',
  'markOfTheTraitor',
  'markOfClarity',
  'markOfAssassin',
] as const
export type MarkId = (typeof MARK_IDS)[number]

/** Every token image id (artifacts, marks, shield, plus the two face-down backs). */
export type TokenImageId = ArtifactId | MarkId | 'shield' | 'artifactBack' | 'markBack'
